const messageHandlerName = "knowsball";

const notificationOpenEvent = "knowsball:notification";

const appsEvent = "knowsball:apps";

interface WebKitMessageHandler {
	postMessage: (message: unknown) => void;
}

declare global {
	interface Window {
		__knowsballPendingNotificationId?: string;
		webkit?: {
			messageHandlers?: Record<string, WebKitMessageHandler | undefined>;
		};
	}
}

interface AuthStateMessage {
	accessToken: string | null;
	type: "auth";
}

export const postAuthState = (accessToken: string | null): void => {
	window.webkit?.messageHandlers?.[messageHandlerName]?.postMessage({
		accessToken,
		type: "auth",
	} satisfies AuthStateMessage);
};

export const subscribeToNotificationOpens = (
	handler: (notificationId: string) => void,
): (() => void) => {
	const listener = (event: Event) => {
		const detail = (event as CustomEvent<{ notificationId?: string }>).detail;

		if (detail?.notificationId) {
			window.__knowsballPendingNotificationId = undefined;
			handler(detail.notificationId);
		}
	};

	window.addEventListener(notificationOpenEvent, listener);

	const pending = window.__knowsballPendingNotificationId;

	if (pending) {
		window.__knowsballPendingNotificationId = undefined;
		handler(pending);
	}

	return () => window.removeEventListener(notificationOpenEvent, listener);
};

export const isNativeShell = (): boolean =>
	Boolean(window.webkit?.messageHandlers?.[messageHandlerName]);

export const postNativeMessage = (message: unknown): boolean => {
	const handler = window.webkit?.messageHandlers?.[messageHandlerName];

	if (!handler) {
		return false;
	}

	handler.postMessage(message);
	return true;
};

let requestCounter = 0;

const createRequestId = (): string => {
	requestCounter += 1;
	return `knowsball-${requestCounter}-${Date.now()}`;
};

const requestTimeoutMs = 5000;

interface NativeRequestOptions<T> {
	type: string;
	method: string;
	payload: Record<string, unknown>;
	event: string;
	errorMessage: string;
	resolve: (detail: Record<string, unknown>) => T | undefined;
}

export const requestNative = <T>({
	type,
	method,
	payload,
	event,
	errorMessage,
	resolve,
}: NativeRequestOptions<T>): Promise<T> =>
	new Promise((promiseResolve, promiseReject) => {
		const id = createRequestId();

		const listener = (eventObject: Event) => {
			const detail = (eventObject as CustomEvent<Record<string, unknown>>)
				.detail;

			if (detail?.id !== id) {
				return;
			}

			cleanup();

			if (detail.type === "error") {
				promiseReject(
					new Error(
						typeof detail.message === "string" ? detail.message : errorMessage,
					),
				);
				return;
			}

			const value = resolve(detail);

			if (value === undefined) {
				promiseReject(new Error(errorMessage));
				return;
			}

			promiseResolve(value);
		};

		const timeout = window.setTimeout(() => {
			cleanup();
			promiseReject(new Error(errorMessage));
		}, requestTimeoutMs);

		const cleanup = () => {
			window.clearTimeout(timeout);
			window.removeEventListener(event, listener);
		};

		window.addEventListener(event, listener);

		if (!postNativeMessage({ type, method, id, ...payload })) {
			cleanup();
			promiseReject(new Error(errorMessage));
		}
	});

export const getInstalledApps = (
	schemes: string[],
): Promise<Record<string, boolean>> =>
	requestNative<Record<string, boolean>>({
		type: "apps",
		method: "installed",
		payload: { schemes },
		event: appsEvent,
		errorMessage: "Unable to check installed apps",
		resolve: (detail) =>
			typeof detail.installed === "object" && detail.installed !== null
				? (detail.installed as Record<string, boolean>)
				: undefined,
	});

export const openExternal = (
	url: string,
	fallbackUrl?: string,
): Promise<boolean> =>
	requestNative<boolean>({
		type: "apps",
		method: "open",
		payload: fallbackUrl ? { url, fallbackUrl } : { url },
		event: appsEvent,
		errorMessage: "Unable to open app",
		resolve: (detail) =>
			typeof detail.opened === "boolean" ? detail.opened : undefined,
	});
